import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import config from '../config';
import { useUploadManager } from '../upload/UploadContext';
import { useAuth } from '../auth/AuthContext';

const UploadPage = () => {
  const { queueFiles, uploads, groups } = useUploadManager();
  const { fetchWithAuth } = useAuth();
  const groupList = React.useMemo(
    () =>
      Object.values(groups)
        .slice()
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [groups],
  );
  const [isDragging, setIsDragging] = React.useState(false);
  const inputRef = React.useRef(null);
  const [uploadPrintful, setUploadPrintful] = React.useState(true);
  const [uploadPrintify, setUploadPrintify] = React.useState(true);
  const [upscaleImage, setUpscaleImage] = React.useState(true);
  const [printifyShops, setPrintifyShops] = React.useState([]);
  const [printifyTemplates, setPrintifyTemplates] = React.useState([]);
  const [selectedShopIds, setSelectedShopIds] = React.useState([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = React.useState([]);
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  const [optionsError, setOptionsError] = React.useState(null);
  const [formError, setFormError] = React.useState(null);

  const toggleShopSelection = React.useCallback((shopId) => {
    setSelectedShopIds((prev) => {
      if (prev.includes(shopId)) {
        return prev.filter((id) => id !== shopId);
      }
      return [...prev, shopId];
    });
  }, []);

  const toggleTemplateSelection = React.useCallback((templateId) => {
    setSelectedTemplateIds((prev) => {
      if (prev.includes(templateId)) {
        return prev.filter((id) => id !== templateId);
      }
      return [...prev, templateId];
    });
  }, []);

  React.useEffect(() => {
    let active = true;
    const loadOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(null);
      try {
        const [shopsResp, templatesResp] = await Promise.all([
          fetchWithAuth(`${config.apiBaseUrl}/printify/shops`),
          fetchWithAuth(`${config.apiBaseUrl}/printify/templates`),
        ]);
        if (!active) {
          return;
        }
        if (!shopsResp.ok) {
          throw new Error('Failed to load Printify shops');
        }
        if (!templatesResp.ok) {
          throw new Error('Failed to load product templates');
        }
        const shopsPayload = await shopsResp.json();
        const templatePayload = await templatesResp.json();
        const shops = Array.isArray(shopsPayload.items) ? shopsPayload.items.filter((shop) => !shop.is_virtual) : [];
        const templates = Array.isArray(templatePayload.items) ? templatePayload.items : [];
        setPrintifyShops(shops);
        setPrintifyTemplates(templates);
        setSelectedShopIds((prev) => {
          if (prev.length || !shops.length) {
            return prev;
          }
          return [shops[0].id];
        });
        setSelectedTemplateIds((prev) => {
          if (prev.length || !templates.length) {
            return prev;
          }
          return [templates[0].id];
        });
      } catch (err) {
        if (active) {
          setOptionsError(err.message || 'Failed to load Printify options');
        }
      } finally {
        if (active) {
          setOptionsLoading(false);
        }
      }
    };
    loadOptions();
    return () => {
      active = false;
    };
  }, [fetchWithAuth]);

  const handleFiles = React.useCallback(
    (fileList) => {
      if (!fileList || fileList.length === 0) {
        return;
      }
      if (uploadPrintify && (selectedShopIds.length === 0 || selectedTemplateIds.length === 0)) {
        setFormError('Select at least one Printify shop and one product template.');
        return;
      }
      setFormError(null);
      const optionsPayload = {
        upload_printful: uploadPrintful,
        upload_printify: uploadPrintify,
        upscale_image: upscaleImage,
        printify_shop_ids: selectedShopIds,
        printify_template_ids: selectedTemplateIds,
      };
      queueFiles(fileList, optionsPayload);
    },
    [queueFiles, uploadPrintful, uploadPrintify, upscaleImage, selectedShopIds, selectedTemplateIds],
  );

  const handleDrop = React.useCallback(
    (event) => {
      event.preventDefault();
      setIsDragging(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragOver = React.useCallback((event) => {
    event.preventDefault();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDragLeave = React.useCallback((event) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragging(false);
  }, []);

  const handleFileInput = React.useCallback(
    (event) => {
      handleFiles(event.target.files);
      event.target.value = '';
    },
    [handleFiles],
  );

  const openFilePicker = React.useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack spacing={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Upload to
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadPrintful}
                  onChange={(event) => setUploadPrintful(event.target.checked)}
                />
              }
              label="Printful"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadPrintify}
                  onChange={(event) => setUploadPrintify(event.target.checked)}
                />
              }
              label="Printify"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={upscaleImage}
                  onChange={(event) => setUpscaleImage(event.target.checked)}
                />
              }
              label="Upscale image"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Choose which services should receive newly uploaded images, and whether a higher-DPI derivative should be created first.
          </Typography>
        </Paper>

        {uploadPrintify ? (
          <>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Printify Shops
              </Typography>
              {optionsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading shops…
                  </Typography>
                </Stack>
              ) : printifyShops.length ? (
                <Stack spacing={0.5}>
                  {printifyShops.map((shop) => (
                    <FormControlLabel
                      key={shop.id}
                      control={
                        <Checkbox
                          checked={selectedShopIds.includes(shop.id)}
                          onChange={() => toggleShopSelection(shop.id)}
                        />
                      }
                      label={`${shop.title || 'Unnamed shop'}${
                        shop.sales_channel ? ` (${shop.sales_channel})` : ''
                      }`}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No Printify shops are available for product creation.
                </Typography>
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Product Templates
              </Typography>
              {optionsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading templates…
                  </Typography>
                </Stack>
              ) : printifyTemplates.length ? (
                <Stack spacing={0.5}>
                  {printifyTemplates.map((template) => (
                    <FormControlLabel
                      key={template.id}
                      control={
                        <Checkbox
                          checked={selectedTemplateIds.includes(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                        />
                      }
                      label={template.template_label || template.title || 'Untitled template'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No templates have been created yet. Create one from the Products page to get started.
                </Typography>
              )}
            </Paper>
          </>
        ) : null}

        {optionsError ? (
          <Alert severity="error" onClose={() => setOptionsError(null)}>
            {optionsError}
          </Alert>
        ) : null}
        {formError ? (
          <Alert severity="warning" onClose={() => setFormError(null)}>
            {formError}
          </Alert>
        ) : null}
      </Stack>

      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFilePicker}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: isDragging ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          textAlign: 'center',
          cursor: 'pointer',
        }}
        role="presentation"
      >
        <CloudUploadIcon color="primary" sx={{ fontSize: 48 }} />
        <Typography variant="h5">Drop images here</Typography>
        <Typography variant="body2" color="text.secondary">
          Drag and drop image files, or click to browse your device.
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileInput}
        />
        <Button
          variant="contained"
          onClick={(event) => {
            event.stopPropagation();
            openFilePicker();
          }}
          sx={{ mt: 2 }}
        >
          Select Files
        </Button>
      </Box>

      {groupList.length > 0 ? (
        <Box sx={{ backgroundColor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="subtitle1" sx={{ px: 3, py: 2 }}>
            Upload Queue
          </Typography>
          <List disablePadding>
            {groupList.map((group) => {
              const files = group.fileIds.map((id) => uploads[id]).filter(Boolean);
              return (
                <Box key={group.id} sx={{ paddingX: 3, paddingBottom: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {group.name}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={group.progress || 0}
                    color={group.status === 'error' ? 'error' : 'primary'}
                    sx={{ mb: 1 }}
                  />
                  <List dense disablePadding>
                    {files.map((entry) => (
                      <ListItem key={entry.id} sx={{ pl: 0 }}>
                        <ListItemText
                          primary={entry.name}
                          secondary={`${entry.type || 'image'} · ${(entry.size / 1024).toFixed(1)} KB`}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 200 }}>
                          <LinearProgress
                            variant="determinate"
                            value={entry.status === 'error' ? entry.progress || 0 : entry.progress || 0}
                            color={entry.status === 'error' ? 'error' : 'primary'}
                          />
                          <Chip
                            size="small"
                            label={
                              entry.status === 'queued'
                                ? 'Queued'
                                : entry.status === 'uploading'
                                ? `Uploading (${entry.progress || 0}%)`
                                : entry.status === 'completed'
                                ? 'Completed'
                                : 'Error'
                            }
                            color={
                              entry.status === 'completed'
                                ? 'success'
                                : entry.status === 'error'
                                ? 'error'
                                : 'default'
                            }
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </List>
        </Box>
      ) : null}
    </Box>
  );
};

export default UploadPage;
